import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";

const chip = (on) =>
  `rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${on ? "border-ink bg-ink text-sand" : "border-ink/20 bg-sand text-ink hover:border-ink"}`;

const ScaleRow = ({ q, value, onChange, max, min = 1 }) => (
  <div>
    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={q.label}>
      {Array.from({ length: max - min + 1 }, (_, i) => i + min).map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          onClick={() => onChange(n)}
          data-testid={`survey-${q.id}-${n}`}
          className={`h-10 min-w-[2.5rem] rounded-md border text-sm font-semibold transition-colors ${value === n ? "border-ink bg-ink text-sand" : "border-ink/20 bg-sand text-ink hover:border-ink"}`}
        >
          {n}
        </button>
      ))}
    </div>
    {(q.low_label || q.type === "nps") && (
      <div className="mt-1.5 flex justify-between text-[12px] text-olive">
        <span>{q.type === "nps" ? "Not likely" : q.low_label}</span>
        <span>{q.type === "nps" ? "Very likely" : q.high_label}</span>
      </div>
    )}
  </div>
);

const Question = ({ q, value, onChange }) => {
  if (q.type === "scale") return <ScaleRow q={q} value={value} onChange={onChange} max={5} />;
  if (q.type === "nps") return <ScaleRow q={q} value={value} onChange={onChange} max={10} min={0} />;
  if (q.type === "single" || q.type === "yesno") {
    const opts = q.type === "yesno" ? ["Yes", "No"] : q.options;
    return (
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={q.label}>
        {opts.map((o) => (
          <button key={o} type="button" role="radio" aria-checked={value === o} onClick={() => onChange(o)} data-testid={`survey-${q.id}-opt`} className={chip(value === o)}>{o}</button>
        ))}
      </div>
    );
  }
  if (q.type === "multi") {
    const cur = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]);
    return (
      <div className="flex flex-wrap gap-2" role="group" aria-label={q.label}>
        {q.options.map((o) => (
          <button key={o} type="button" aria-pressed={cur.includes(o)} onClick={() => toggle(o)} data-testid={`survey-${q.id}-opt`} className={chip(cur.includes(o))}>{o}</button>
        ))}
      </div>
    );
  }
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      maxLength={1000}
      data-testid={`survey-${q.id}-text`}
      className="w-full rounded-md border border-border bg-sand px-3 py-2 text-base text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none"
      placeholder="Your words, in your own way."
    />
  );
};

/**
 * Renders any survey config {questions[]} and submits {answers}. Validation of
 * required questions happens here; the server re-checks.
 */
export const SurveyForm = ({ survey, onSubmit, submitLabel = "Send my answers", extraFields = null, compact = false }) => {
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  const missing = survey.questions.filter((q) => q.required && (answers[q.id] === undefined || answers[q.id] === "" || (Array.isArray(answers[q.id]) && !answers[q.id].length)));

  const submit = async (e) => {
    e.preventDefault();
    if (missing.length) {
      setError(`Please answer: ${missing[0].label}`);
      document.getElementById(`survey-q-${missing[0].id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setError("");
    setBusy(true);
    try {
      await onSubmit(answers);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send your answers. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="survey-form" className={compact ? "space-y-5" : "space-y-7"}>
      {survey.questions.map((q, i) => (
        <div key={q.id} id={`survey-q-${q.id}`} data-testid={`survey-question-${q.id}`}>
          <p className="text-base font-semibold leading-snug text-ink">
            <span className="mr-2 font-mono text-[12px] text-olive">{String(i + 1).padStart(2, "0")}</span>
            {q.label}
            {q.required ? <span className="ml-1 text-tangerine" aria-hidden>*</span> : <span className="ml-2 text-[12px] font-normal text-olive">optional</span>}
          </p>
          {q.help && <p className="mt-1 text-[13px] text-olive">{q.help}</p>}
          <div className="mt-3">
            <Question q={q} value={answers[q.id]} onChange={(v) => set(q.id, v)} />
          </div>
        </div>
      ))}
      {extraFields}
      {error && <p role="alert" data-testid="survey-error" className="text-sm text-scarlet">{error}</p>}
      <button
        type="submit"
        disabled={busy}
        data-testid="survey-submit"
        className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {submitLabel} {!busy && <ArrowRight className="h-4 w-4" strokeWidth={2} />}
      </button>
    </form>
  );
};
